import { useCallback, useEffect, useState } from "react";

import type {
  LastPaginationParams,
  LastParamsPaginationProps,
} from "@/shared/types";

const EMPTY_LAST_PARAMS: LastPaginationParams = {
  lastCreatedAt: null,
  lastId: null,
};

type UseLastParamsPaginationProps<TData> = Pick<
  LastParamsPaginationProps<TData>,
  "getLastParams" | "pageSize" | "resetKey"
>;

export const useLastParamsPagination = <TData,>({
  getLastParams,
  pageSize,
  resetKey,
}: UseLastParamsPaginationProps<TData>) => {
  const [lastParamsState, setLastParamsState] = useState<{
    resetKey: string;
    value: LastPaginationParams;
  }>({
    resetKey: "",
    value: EMPTY_LAST_PARAMS,
  });

  const lastParams =
    lastParamsState.resetKey === resetKey
      ? lastParamsState.value
      : EMPTY_LAST_PARAMS;

  const setLastParams = useCallback(
    (nextLastParams: LastPaginationParams) => {
      setLastParamsState({
        resetKey,
        value: nextLastParams,
      });
    },
    [resetKey],
  );

  useEffect(() => {
    setLastParamsState((prevState) =>
      prevState.resetKey === resetKey
        ? prevState
        : {
            resetKey,
            value: EMPTY_LAST_PARAMS,
          },
    );
  }, [resetKey]);

  const getLastParamsPagination = useCallback(
    (total: number): LastParamsPaginationProps<TData> => ({
      getLastParams,
      pageSize,
      resetKey,
      setLastParams,
      total,
    }),
    [getLastParams, pageSize, resetKey, setLastParams],
  );

  return {
    getLastParamsPagination,
    lastParams,
    setLastParams,
  };
};
